import { uuidv7 } from 'uuidv7';
import { AmmsMessageValidationError, getAmmsMessageControlId } from '../../hl7/message.js';
import { zql } from '../../schema.js';
import { itemsDetail, valueItem } from '../../util/event-log-items.js';
import { EventLogs } from '../../util/event_logs.js';
import { addEventLog, EventLogResources } from '../util.js';
/** Resolves a user id into the `{username, firstName, lastName}` shape used by ORC provider composites. */
export async function resolveAmmsOrcProvider(tx, userId) {
    const user = userId ? await tx.run(zql.user.where('id', '=', userId).one()) : undefined;
    return {
        username: user?.username ?? '',
        firstName: user?.firstName ?? '',
        lastName: user?.lastName ?? '',
    };
}
/** Counts prior outbound HL7 messages logged for a resource (e.g. a prescription or rest vial). */
export async function countPriorAmmsLogs(tx, resourceId) {
    const priorLogs = await tx.run(zql.HL7EventLog.where('direction', '=', 'OUTBOUND'));
    return priorLogs.filter(log => log.resourceRef?.some(ref => ref.id === resourceId)).length + 1;
}
/** Whether any outbound HL7 message has already been logged for any of the given resources. */
export async function hasPriorAmmsLog(tx, resourceIds) {
    const ids = new Set(resourceIds);
    const priorLogs = await tx.run(zql.HL7EventLog.where('direction', '=', 'OUTBOUND'));
    return priorLogs.some(log => log.resourceRef?.some(ref => ids.has(ref.id)));
}
/**
 * Next value of `hl7_outbound_control_seq`, used for the numeric part of MSH-10. Server-only:
 * outbound AMMS messages are only ever built in the authoritative (server) transaction.
 */
export async function nextAmmsControlSequence(tx) {
    if (tx.location !== 'server') {
        throw new Error('AMMS control IDs can only be generated on the server.');
    }
    const [row] = [...(await tx.dbTransaction.query(`SELECT nextval('hl7_outbound_control_seq') AS value`, []))];
    return Number(row?.value);
}
/**
 * Builds an outbound AMMS message and logs it as a pending `hl7_event_log` row (status null), with
 * its MSH-10 in `messageControlId` so the ACK (MSA-2) can be matched back to it. If the build
 * fails validation (`AmmsMessageValidationError`, e.g. a blank unit), a FAILED row with the reason
 * is logged instead, so the message is never sent but stays visible for review. With a
 * `skipReason`, the built message is logged as SKIPPED with that reason: the sender only picks up
 * rows with no status, so it is never sent or retried.
 */
export async function logAmmsOutboundMessage(tx, params) {
    const { build, resourceRef, now, skipReason } = params;
    let message;
    try {
        message = await build();
    }
    catch (error) {
        if (!(error instanceof AmmsMessageValidationError))
            throw error;
        await tx.mutate.HL7EventLog.insert({
            id: uuidv7(),
            message: '',
            createdAt: now,
            direction: 'OUTBOUND',
            status: 'FAILED',
            errorMessage: error.message,
            resourceRef,
        });
        if (tx.location === 'server') {
            const sequenceId = resourceRef.find(ref => ref.resource === EventLogResources.patientTherapySequence)?.id;
            const sequence = sequenceId ? await tx.run(zql.patientTherapySequence.where('id', sequenceId).one()) : undefined;
            await addEventLog(tx, {
                id: uuidv7(),
                domain: 'hl7',
                event: EventLogs.HL7_MESSAGE_SEND_FAILED,
                status: 'FAILED',
                performedAt: now,
                // the message could not be built, so it has no type or control id yet
                details: {
                    messageType: '-',
                    controlId: '-',
                    error: error.message.slice(0, 300),
                    ...itemsDetail(valueItem('prescription', sequence?.internalId)),
                },
            });
        }
        return;
    }
    await tx.mutate.HL7EventLog.insert({
        id: uuidv7(),
        message: message.toString(),
        messageControlId: getAmmsMessageControlId(message),
        createdAt: now,
        direction: 'OUTBOUND',
        resourceRef,
        ...(skipReason ? { status: 'SKIPPED', errorMessage: skipReason } : {}),
    });
}
//# sourceMappingURL=amms.js.map